import mapboxgl from 'mapbox-gl';

const {
    accessToken = null,
    mapboxStyleUsername = null,
    mapboxStyleId = null,
} = window.MapboxConfig;
const mapElement = document.getElementById('locations-map');
const mapContent = mapElement.dataset.content ?? 'wells';
const contentTypes = {
    wells: {
        endpoint: '/actions/location-api/location/all-wells',
        marker: '/dist/images/well-marker.png',
        popupContent: ['address'],
        color: '#ffffff',
        background: '#1D70B7',
        defaultSearchZoomLevel: 12,
    },
    facilities: {
        endpoint: '/actions/location-api/location/all-facilities',
        marker: '/dist/images/facility-marker.png',
        popupContent: ['category', 'description'],
        color: '#163F41',
        background: '#71bf44',
        defaultSearchZoomLevel: 11,
    },
};

mapboxgl.accessToken = accessToken;

const fetchLocations = () => {
    return fetch(contentTypes[mapContent].endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(response => response.json());
};

const initializeMap = async locationsPromise => {
    mapElement.hidden = false;

    const map = new mapboxgl.Map({
        container: 'locations-map',
        style: `mapbox://styles/${mapboxStyleUsername}/${mapboxStyleId}`,
        center: [10, 51],
        zoom: 5.5,
        dragRotate: false,
        touchPitch: false,
    });

    const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
            enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showUserHeading: true,
    });

    window.registerGeolocationCallback((coordinates, source = 'search') => {
        if (source === 'geolocation') {
            geolocateControl.trigger();
        }

        if (source === 'search') {
            map.flyTo({
                center: new mapboxgl.LngLat(coordinates.longitude, coordinates.latitude),
                zoom: contentTypes[mapContent].defaultSearchZoomLevel,
                duration: 1000,
            });
        }
    });

    map.on('load', async () => {
        const data = await locationsPromise;
        const locations = data.result;
        const geojson = {
            type: 'FeatureCollection',
            features: locations.map(location => ({
                type: 'Feature',
                properties: {
                    title: location.title,
                    description: location.description,
                    category: location.category,
                    street: location.location.street1,
                    city: location.location.city,
                    url: location.url,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [location.location.lng, location.location.lat],
                },
            })),
        };

        map.addControl(new mapboxgl.NavigationControl());

        map.addControl(geolocateControl);

        map.touchZoomRotate.disableRotation();

        map.addSource('locations', {
            type: 'geojson',
            data: geojson,
            cluster: true,
            clusterRadius: 60,
        });

        map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'locations',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': contentTypes[mapContent].background,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff',
                'circle-radius': ['step', ['get', 'point_count'], 20, 20, 30, 100, 40],
            },
        });

        const bounds = new mapboxgl.LngLatBounds();
        locations.forEach(({ location: { lat, lng } }) => bounds.extend([lng, lat]));
        map.fitBounds(bounds, { padding: 80 });

        map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'locations',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['Source Sans Pro SemiBold', 'Arial Unicode MS Regular'],
                'text-size': 18,
            },
            paint: {
                'text-color': contentTypes[mapContent].color,
            },
        });

        map.loadImage(contentTypes[mapContent].marker, function (error, image) {
            if (error) {
                map.addLayer({
                    id: 'marker',
                    type: 'circle',
                    source: 'locations',
                    filter: ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': contentTypes[mapContent].background,
                        'circle-radius': 8,
                        'circle-stroke-width': 1,
                        'circle-stroke-color': '#fff',
                    },
                });
                throw error;
            }

            map.addImage('custom-marker', image);
            map.addLayer({
                id: 'marker',
                type: 'symbol',
                source: 'locations',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': 'custom-marker',
                    'icon-size': 0.5,
                    'icon-anchor': 'bottom',
                },
            });
        });

        map.on('click', 'marker', e => {
            const coordinates = e.features[0].geometry.coordinates.slice();
            const { title, category, description, url, street, city } = e.features[0].properties;
            const content = contentTypes[mapContent].popupContent
                .map(content => {
                    let htmlString = '';
                    if (content === 'category' && category) {
                        htmlString += `<p class="map-popup__category">${category}</p>`;
                    }
                    if (content === 'description' && description) {
                        htmlString += `<p class="map-popup__text">${description}</p>`;
                    }
                    if (content === 'address' && (street || city)) {
                        htmlString += `<p class="map-popup__text">${(street ? street + ', ' : '') + (city ? city : '')}</p>`;
                    }
                    return htmlString;
                })
                .join('');

            const markerWidth = 39;
            const markerHeight = 60;
            const offset = 5;

            new mapboxgl.Popup({
                maxWidth: 'none',
                offset: {
                    top: [0, offset],
                    'top-left': [0, offset],
                    'top-right': [0, offset],
                    bottom: [0, -markerHeight - offset],
                    'bottom-left': [0, -markerHeight - offset],
                    'bottom-right': [0, -markerHeight - offset],
                    left: [markerWidth / 2 + offset, -markerHeight / 2],
                    right: [-markerWidth / 2 - offset, -markerHeight / 2],
                },
            })
                .setLngLat(coordinates)
                .setHTML(
                    `<div class="locations-map__popup map-popup">
                        <div class="map-popup__text-wrapper">
                            <p class="map-popup__text">
                                <a class="map-popup__link" href="${url}">${title}</a>
                            </p>
                            ${content}
                        </div>
                        <div class="map-popup__icon-wrapper" aria-hidden="true">
                            <svg class="map-popup__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>
                        </div>
                    </div>`,
                )
                .addTo(map);
        });

        map.on('click', 'clusters', e => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['clusters'],
            });
            const clusterId = features[0].properties.cluster_id;
            map.getSource('locations').getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                });
            });
        });

        map.on('mouseenter', 'clusters', () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'clusters', () => {
            map.getCanvas().style.cursor = '';
        });

        map.on('mouseenter', 'marker', () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'marker', () => {
            map.getCanvas().style.cursor = '';
        });
    });
};

const initializeMapAfterConsent = () => {
    const locationsPromise = fetchLocations();
    const consentManager = window.klaro.getManager();

    if (consentManager.getConsent('mapbox')) {
        return initializeMap(locationsPromise);
    }

    consentManager.watch({
        update(obj, name, data) {
            if (name !== 'applyConsents') return;
            if (!obj.states.mapbox) return;
            initializeMap(locationsPromise);
        },
    });
};

initializeMapAfterConsent();
